<template>
    <main class="flex-grow-1 px-1 py-1">

      <!-- ===================== -->
      <!-- TOP STATS -->
      <!-- ===================== -->
      <div class="row g-3 mb-4">

        <!-- Profile Completion -->
        <div class="col-md-3">
          <div class="glassy-table border rounded-lg p-3 shadow-sm h-100">
            <small class="text-muted fw-bold d-block mb-2">PROFILE COMPLETION</small>
            <h3 class="fw-bold mb-1">50%</h3>
            <small class="text-muted d-block mb-3">3 steps remaining</small>

            <div class="progress" style="height:6px;">
              <div class="progress-bar bg-success" style="width:50%"></div>
            </div>
          </div>
        </div>

        <!-- Compliance -->
        <div class="col-md-3">
          <div class="glassy-table border rounded-lg p-3 shadow-sm h-100">
            <small class="text-muted fw-bold d-block mb-2">COMPLIANCE RATE</small>
            <h3 class="fw-bold text-success mb-1">57%</h3>
            <small class="text-muted d-block mb-3">4 of 7 entities compliant</small>

            <div class="d-flex flex-wrap gap-2">
              <span class="badge bg-success text-white border border-success">4 Clear</span>
              <span class="badge bg-danger text-white border border-danger">2 Non-Compliant</span>
              <span class="badge bg-warning text-white border border-warning">1 Pending</span>
            </div>
          </div>
        </div>

        <!-- Open Tasks -->
        <div class="col-md-3">
          <div class="glassy-table border rounded-lg p-3 shadow-sm h-100">
            <small class="text-muted fw-bold d-block mb-2">OPEN TASKS</small>
            <h3 class="fw-bold text-danger mb-1">5</h3>
            <small class="text-muted d-block mb-3">Tasks requiring action</small>

            <div class="d-flex flex-wrap gap-2">
              <span class="badge bg-danger text-white border border-danger">3 Overdue</span>
              <span class="badge bg-warning text-white border border-warning">2 Due Soon</span>
            </div>
          </div>
        </div>

        <!-- Documents -->
        <div class="col-md-3">
          <div class="glassy-table border rounded-lg p-3 shadow-sm h-100">
            <small class="text-muted fw-bold d-block mb-2">DOCUMENTS</small>
            <h3 class="fw-bold mb-1">8<small class="text-muted fs-6">/10</small></h3>
            <small class="text-muted d-block mb-3">80% of required docs uploaded</small>

            <div class="d-flex flex-wrap gap-2">
              <span class="badge bg-danger text-white border border-danger">2 Missing</span>
              <span class="badge bg-warning text-white border border-warning">1 Pending Sign-off</span>
            </div>
          </div>
        </div>

      </div>

      <!-- ===================== -->
      <!-- MAIN GRID -->
      <!-- ===================== -->
      <div class="row g-4">

        <!-- ACTION SECTION -->
        <div class="col-lg-8">
          <div class="glassy-table border rounded-lg shadow-sm h-100">

            <div
              class="d-flex justify-content-between align-items-center p-4 border-bottom cursor-pointer"
              role="button"
              @click="show_actions = !show_actions"
            >
              <h6 class="fw-bold m-0 text-danger">
                <i class="fas fa-circle fs-6 me-2"></i>
                What Needs To Happen Now
              </h6>
              <i
                class="fas text-muted transition"
                :class="show_actions ? 'fa-chevron-up' : 'fa-chevron-down'">
              </i>
            </div>

            <div v-if="show_actions" class="p-4">

              <div v-for="task in tasks" :key="task.id"
                   class="d-flex justify-content-between align-items-start border-bottom pb-4 mb-4 border-dark">

                <div class="d-flex gap-3">

                  <!-- Status Dot -->
                  <div class="mt-2">
                    <span class="rounded-circle d-inline-block"
                          :class="{
                            'bg-danger': task.priority === 'red',
                            'bg-warning': task.priority === 'orange'
                          }"
                          style="width:10px;height:10px;"></span>
                  </div>

                  <!-- Task Info -->
                  <div>
                    <h6 class="fw-bold mb-2">{{ task.title }}</h6>
                    <p class="text-dark small mb-3" style="max-width:500px;">
                      {{ task.description }}
                    </p>

                    <div class="d-flex flex-wrap gap-2">
                      <span
                        class="badge fw-semibold"
                        :class="{
                          'bg-danger text-white': task.priority === 'red',
                          'bg-warning text-white': task.priority === 'orange'
                        }">
                        {{ task.deadline }}
                      </span>

                      <span v-for="tag in task.tags"
                            :key="tag"
                            class="badge bg-primary text-white">
                        {{ tag }}
                      </span>
                    </div>
                  </div>
                </div>

                <!-- Action Button -->
                <button class="btn btn-primary-dark text-xs rounded"
                        :class="task.priority === 'red'
                          ? 'btn-success'
                          : 'btn-outline-secondary'">
                  {{ task.btnText }}
                </button>

              </div>

            </div>
          </div>
        </div>

        <!-- RIGHT COLUMN -->
        <div class="col-lg-4">

          <!-- Trust Details -->
          <div class="glassy-table border rounded-lg shadow-sm mb-4">
            <div
              class="d-flex justify-content-between align-items-center p-4 border-bottom cursor-pointer"
              role="button"
              @click="show_trust_details  = !show_trust_details "
            >
              <h6 class="fw-bold m-0 text-primary-blue">
                <i class="fas fa-circle fs-6 me-2"></i>
                Trust Details
              </h6>
              <i
                class="fas text-muted transition"
                :class="show_trust_details ? 'fa-chevron-up' : 'fa-chevron-down'">
               </i>
            </div>

            <div v-if="show_trust_details" class="p-4">

              <div v-for="(val, label) in trust_details"
                   :key="label"
                   class="d-flex justify-content-between border-bottom border-dark py-2 small">

                <span class="text-dark">{{ label }}</span>
                <span class="fw-semibold"
                      :class="label === 'Ind. Trustee' ? 'badge bg-success-subtle text-success border border-success' : ''">
                  {{ val }}
                </span>
              </div>

              <!-- Profile Progress -->
              <div class="pt-3">
                <small class="text-dark d-block mb-2">Profile completion</small>

                <div class="d-flex align-items-center gap-2">
                  <div class="progress flex-grow-1" style="height:6px;">
                    <div class="progress-bar bg-success" style="width:50%"></div>
                  </div>
                  <span class="fw-bold text-success small">50%</span>
                </div>
              </div>

            </div>
          </div>

          <!-- Assets -->
          <div class="glassy-table border rounded-lg shadow-sm p-4">
            <small class="fw-bold text-muted d-block mb-3">ASSETS HELD IN TRUST</small>

            <div v-for="asset in assets"
                 :key="asset.name"
                 class="d-flex justify-content-between align-items-center border-bottom py-2 small border-dark">

              <div class="d-flex align-items-center gap-2">
                <div class="bg-primary text-white rounded-3 d-inline-flex p-2">
                  <i :class="asset.icon" class="fs-6"></i>
                </div>

                <span>{{ asset.name }}</span>
              </div>

              <span class="fw-bold">
                {{ asset.count }} {{ asset.unit }}
              </span>
            </div>

          </div>

        </div>

      </div>

    </main>
</template>


<script>
export default {
  data() {
    return {
      show_actions: true,
      show_trust_details: true,
      tasks: [
        {
          id: 1,
          priority: 'red',
          title: 'Upload FICA documents for John Mokoena',
          description: 'Trustee is missing certified ID copy and proof of address. These are required under FICA regulations.',
          deadline: '14 days overdue',
          deadlineClass: 'tag-red',
          tags: ['Trustee'],
          btnText: 'Upload Now',
          btnClass: 'btn-green'
        },
        {
          id: 2,
          priority: 'red',
          title: 'Upload FICA documents for Sarah Dlamini',
          description: 'Proof of address is outstanding. ID has been verified but FICA compliance requires both.',
          deadline: '7 days overdue',
          deadlineClass: 'tag-red',
          tags: ['Trustee'],
          btnText: 'Upload Now',
          btnClass: 'btn-green'
        },
        {
          id: 3,
          priority: 'orange',
          title: 'Trust Deed Amendment — awaiting signatures',
          description: 'The 2025 amendment has been uploaded but requires wet signatures from all 3 trustees.',
          deadline: 'Due 23 Feb 2026',
          deadlineClass: 'tag-orange',
          tags: ['All Trustees'],
          btnText: 'Review & Sign',
          btnClass: 'btn-outline'
        }
      ],
      trust_details: {
        'Type': 'Discretionary Family Trust',
        'Province': 'Gauteng',
        'Founder': 'Peter Vilakazi',
        'Trustees': '3 registered',
        'Beneficiaries': '3 registered (1 pending KYC)',
        'Ind. Trustee': 'Mandla Nkosi ✓',
        'Registered': '12 Jan 2023',
        'Next Review': '15 Mar 2026'
      },
      assets: [
        { icon: 'fas fa-home', name: 'Residential Property', count: 3, unit: 'properties' },
        { icon: 'fas fa-building', name: 'Commercial Property', count: 1, unit: 'property' },
        { icon: 'fas fa-chart-line', name: 'Listed Equities', count: 12, unit: 'holdings' }
      ]

    }
  }
}
</script>
